<template>
  <nav 
    ref="navRef"
    class="fixed top-0 w-full z-50 px-6 py-3 nav-transition"
    :class="navVisible ? 'nav-visible' : 'nav-hidden'"
  >
    <div class="max-w-6xl mx-auto flex items-center justify-between nav-blur-container rounded-full px-5 py-2 border border-white/40 relative group" :class="{ 'menu-open': menuOpen }">
      <div class="nav-blur-backdrop"></div>
      <div class="relative z-10 flex items-center justify-between w-full">
        <router-link to="/" class="flex items-center gap-3">
          <img src="/images/safe_haven_logo_clear.png" alt="Safe Haven Wellness Group" class="h-16 w-auto" />
          <h1 class="text-xl font-bold tracking-tight italic text-[var(--forest)]">Safe Haven</h1>
        </router-link>
        <div class="hidden md:flex items-center gap-8">
          <a
            v-if="isHomePage"
            class="text-sm font-semibold text-[var(--forest)] hover:text-[var(--terracotta)]"
            href="#story"
          >Our Story</a>
          <router-link
            v-else
            :to="{ path: '/', hash: '#story' }"
            class="text-sm font-semibold text-[var(--forest)] hover:text-[var(--terracotta)]"
          >Our Story</router-link>
          <router-link to="/interventions" class="text-sm font-semibold text-[var(--forest)] hover:text-[var(--terracotta)]">Interventions</router-link>
          <router-link to="/about" class="text-sm font-semibold text-[var(--forest)] hover:text-[var(--terracotta)]">About Us</router-link>
          <router-link to="/case-studies" class="text-sm font-semibold text-[var(--forest)] hover:text-[var(--terracotta)]">News & Media</router-link>
          <button @click="openJoinModal" class="btn-organic bg-[var(--forest)] text-white text-sm font-bold shadow-lg">Join Community</button>
        </div>
        <!-- Mobile hamburger button -->
        <button 
          @click="toggleMenu"
          class="md:hidden relative z-[70] w-10 h-10 flex flex-col justify-center items-center gap-1.5 focus:outline-none cursor-pointer"
          aria-label="Toggle menu"
        >
          <span class="hamburger-line" :class="{ 'hamburger-open': menuOpen }"></span>
          <span class="hamburger-line" :class="{ 'hamburger-open': menuOpen }"></span>
          <span class="hamburger-line" :class="{ 'hamburger-open': menuOpen }"></span>
        </button>
      </div>
    </div>
  </nav>

  <!-- Mobile menu half-sheet + overlay -->
  <div
    class="md:hidden fixed inset-0 z-[60]"
    :class="menuOpen ? 'pointer-events-auto' : 'pointer-events-none'"
  >
    <!-- Overlay fills remaining space -->
    <div
      @click="closeMenu"
      class="absolute inset-0 transition-opacity duration-300 ease-out"
      :class="menuOpen ? 'opacity-100' : 'opacity-0'"
    >
      <div class="absolute inset-0 bg-black/30 backdrop-blur-sm"></div>
    </div>

    <!-- Panel (opens halfway) -->
    <div
      class="absolute top-0 left-0 right-0 transition-transform duration-300 ease-out mobile-menu-half"
      :class="menuOpen ? 'translate-y-0' : '-translate-y-full'"
    >
      <div class="bg-[var(--cream)] mobile-menu-solid rounded-b-[32px] shadow-2xl flex flex-col max-h-[65vh] overflow-hidden">
        <!-- Drag handle indicator -->
        <div class="flex justify-center pt-3 pb-2">
          <div class="w-12 h-1.5 bg-[var(--moss)]/30 rounded-full"></div>
        </div>
        
        <!-- Menu header -->
        <div class="flex items-center justify-between px-6 pb-4 border-b border-[var(--moss)]/10">
          <div class="flex items-center gap-3">
            <img src="/images/safe_haven_logo_clear.png" alt="Safe Haven" class="h-8 w-auto" />
            <h2 class="text-lg font-bold italic text-[var(--forest)]">Navigation</h2>
          </div>
          <button 
            @click="closeMenu"
            class="w-9 h-9 flex items-center justify-center rounded-full bg-[var(--sand)]/30 hover:bg-[var(--sand)]/50 active:scale-95 transition-all cursor-pointer"
            aria-label="Close menu"
          >
            <span class="material-symbols-outlined text-[var(--forest)] text-lg">close</span>
          </button>
        </div>
        
        <!-- Menu content -->
        <div class="flex-1 overflow-y-auto overscroll-contain px-4 py-4">
          <nav class="flex flex-col gap-1.5">
            <a
              v-if="isHomePage"
              @click="closeMenu"
              class="group px-5 py-4 text-base font-semibold italic text-[var(--forest)] hover:text-[var(--terracotta)] hover:bg-[var(--sand)]/40 active:bg-[var(--sand)]/50 transition-all rounded-xl mobile-menu-item"
              href="#story"
            >
              <span class="flex items-center justify-between">
                <span>Our Story</span>
                <span class="material-symbols-outlined text-sm text-[var(--moss)]/40 group-hover:text-[var(--terracotta)] group-hover:translate-x-1 transition-all">arrow_forward</span>
              </span>
            </a>
            <router-link
              v-else
              :to="{ path: '/', hash: '#story' }"
              @click="closeMenu"
              class="group px-5 py-4 text-base font-semibold italic text-[var(--forest)] hover:text-[var(--terracotta)] hover:bg-[var(--sand)]/40 active:bg-[var(--sand)]/50 transition-all rounded-xl mobile-menu-item"
            >
              <span class="flex items-center justify-between">
                <span>Our Story</span>
                <span class="material-symbols-outlined text-sm text-[var(--moss)]/40 group-hover:text-[var(--terracotta)] group-hover:translate-x-1 transition-all">arrow_forward</span>
              </span>
            </router-link>
            <router-link
              to="/interventions"
              @click="closeMenu"
              class="group px-5 py-4 text-base font-semibold italic text-[var(--forest)] hover:text-[var(--terracotta)] hover:bg-[var(--sand)]/40 active:bg-[var(--sand)]/50 transition-all rounded-xl mobile-menu-item"
            >
              <span class="flex items-center justify-between">
                <span>Interventions</span>
                <span class="material-symbols-outlined text-sm text-[var(--moss)]/40 group-hover:text-[var(--terracotta)] group-hover:translate-x-1 transition-all">arrow_forward</span>
              </span>
            </router-link>
            <router-link
              to="/about"
              @click="closeMenu"
              class="group px-5 py-4 text-base font-semibold italic text-[var(--forest)] hover:text-[var(--terracotta)] hover:bg-[var(--sand)]/40 active:bg-[var(--sand)]/50 transition-all rounded-xl mobile-menu-item"
            >
              <span class="flex items-center justify-between">
                <span>About Us</span>
                <span class="material-symbols-outlined text-sm text-[var(--moss)]/40 group-hover:text-[var(--terracotta)] group-hover:translate-x-1 transition-all">arrow_forward</span>
              </span>
            </router-link>
            <router-link
              to="/case-studies"
              @click="closeMenu"
              class="group px-5 py-4 text-base font-semibold italic text-[var(--forest)] hover:text-[var(--terracotta)] hover:bg-[var(--sand)]/40 active:bg-[var(--sand)]/50 transition-all rounded-xl mobile-menu-item"
            >
              <span class="flex items-center justify-between">
                <span>News & Media</span>
                <span class="material-symbols-outlined text-sm text-[var(--moss)]/40 group-hover:text-[var(--terracotta)] group-hover:translate-x-1 transition-all">arrow_forward</span>
              </span>
            </router-link>
          </nav>
        </div>
        
        <!-- Menu footer -->
        <div class="px-4 pb-6 pt-4 border-t border-[var(--moss)]/10 bg-gradient-to-t from-[var(--sand)]/20 to-transparent">
          <button 
            @click="openJoinModalAndClose"
            class="w-full btn-organic bg-[var(--forest)] text-white text-base font-bold shadow-lg py-4 rounded-xl hover:bg-[var(--forest)]/90 active:scale-[0.98] transition-all cursor-pointer"
          >
            Join Community
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, onUnmounted, ref, computed } from 'vue'
import { useRoute } from 'vue-router'

export default {
  name: 'AppHeader',
  setup() {
    const route = useRoute()
    const navRef = ref(null)
    const navVisible = ref(true)
    const lastScrollY = ref(0)
    const scrollThreshold = 10
    const menuOpen = ref(false)

    const isHomePage = computed(() => route.path === '/')

    const handleScroll = () => {
      const currentScrollY = window.scrollY || window.pageYOffset

      if (currentScrollY < 50) {
        navVisible.value = true
        lastScrollY.value = currentScrollY
        return
      }

      if (Math.abs(currentScrollY - lastScrollY.value) < scrollThreshold) {
        return
      }

      if (currentScrollY > lastScrollY.value && currentScrollY > 100) {
        navVisible.value = false
      } else {
        navVisible.value = true
      }

      lastScrollY.value = currentScrollY
    }

    const toggleMenu = () => {
      menuOpen.value = !menuOpen.value
    }

    const closeMenu = () => {
      menuOpen.value = false
    }

    const openJoinModal = () => {
      // Emit event to parent to open modal
      window.dispatchEvent(new CustomEvent('open-join-modal'))
    }

    const openJoinModalAndClose = () => {
      closeMenu()
      openJoinModal()
    }

    const handleSmoothScroll = (e) => {
      const href = e.target.getAttribute('href')
      if (href && href.startsWith('#')) {
        e.preventDefault()
        const targetId = href.substring(1)
        const targetElement = document.getElementById(targetId)
        if (targetElement) {
          const offset = 100
          const targetPosition = targetElement.offsetTop - offset
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          })
          closeMenu()
        }
      }
    }

    onMounted(() => {
      window.addEventListener('scroll', handleScroll, { passive: true })
      lastScrollY.value = window.scrollY || window.pageYOffset

      const navLinks = document.querySelectorAll('a[href^="#"]')
      navLinks.forEach(link => {
        link.addEventListener('click', handleSmoothScroll)
      })
    })

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll)
      const navLinks = document.querySelectorAll('a[href^="#"]')
      navLinks.forEach(link => {
        link.removeEventListener('click', handleSmoothScroll)
      })
    })

    return {
      navRef,
      navVisible,
      menuOpen,
      isHomePage,
      toggleMenu,
      closeMenu,
      openJoinModal,
      openJoinModalAndClose
    }
  }
}
</script>

<style scoped>
.nav-transition {
  transform: translateY(0);
  opacity: 1;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform, opacity;
}

.nav-visible {
  transform: translateY(0);
  opacity: 1;
}

.nav-hidden {
  transform: translateY(-120px);
  opacity: 0;
}

.nav-blur-container {
  position: relative;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.1);
  transition: background-color 0.3s ease;
}

.nav-blur-container:hover {
  background-color: rgba(255, 255, 255, 0.95);
}

.nav-blur-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  background: rgba(255, 255, 255, 0.25);
  z-index: 1;
  transition: opacity 0.3s ease, backdrop-filter 0.3s ease;
}

.nav-blur-container:hover .nav-blur-backdrop {
  opacity: 0;
  backdrop-filter: blur(0px);
  -webkit-backdrop-filter: blur(0px);
}

.hamburger-line {
  width: 28px;
  height: 3px;
  background-color: var(--forest);
  border-radius: 2px;
  transition: all 0.3s ease-in-out;
  transform-origin: center;
}

.hamburger-line.hamburger-open:nth-child(1) {
  transform: translateY(8px) rotate(45deg);
}

.hamburger-line.hamburger-open:nth-child(2) {
  opacity: 0;
  transform: scaleX(0);
}

.hamburger-line.hamburger-open:nth-child(3) {
  transform: translateY(-8px) rotate(-45deg);
}

.mobile-menu-half {
  transform-origin: top center;
}

.mobile-menu-solid {
  background-color: var(--cream) !important;
  opacity: 1 !important;
}

.mobile-menu-item {
  position: relative;
  min-height: 52px;
  display: flex;
  align-items: center;
}

.mobile-menu-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%) scaleY(0);
  width: 3px;
  height: 60%;
  background: linear-gradient(to bottom, var(--terracotta), var(--moss));
  border-radius: 0 2px 2px 0;
  transition: transform 0.2s ease;
}

.mobile-menu-item:hover::before,
.mobile-menu-item:active::before {
  transform: translateY(-50%) scaleY(1);
}
</style>
